import { FC, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { Plus, Trash02 } from "untitledui-js-base";
import { ImageResolution, useImageResizer } from "./hooks/useImageResizer";
import { useUploadQueueWithProgress } from "./hooks/useUploadQueueWithProgress";
import { HugeiconsIcon } from "@hugeicons/react";
import { Image02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { SectionCard } from "shared/app/SectionCard";
import { getPropertyPresigned } from "../AddProperty/untils";
import { instance } from "api/apiBase";
import { AssetViewer } from "shared/app/AssetViewer";
import { PhotoWithStatus, SUPPORTED_RESOLUTIONS } from "./types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

const VALID_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg"];

interface PhotoItemProps {
  isDefault: boolean;
  onMakeDefault: () => void;
  onDelete: () => void;
  onViewPhoto: () => void;
  photo: PhotoWithStatus;
  isMakingDefaultLoading?: boolean;
  isDeletingLoading?: boolean;
  disabled?: boolean;
}

const PhotoItem: FC<PhotoItemProps> = ({
  isDefault,
  onMakeDefault,
  onDelete,
  onViewPhoto,
  photo,
  isMakingDefaultLoading,
  isDeletingLoading,
  disabled = false
}) => {
  const isUploading = photo.status === "uploading";
  const isError = photo.status === "error";
  const isWaiting = photo.status === "waiting";

  return (
    <div className="relative group h-32">
      <img
        src={photo.previewUrl}
        alt="Property photo"
        className={`w-full h-full object-cover cursor-pointer transition-opacity ${
          isUploading || isWaiting ? "opacity-70" : "opacity-100"
        }`}
        onClick={onViewPhoto}
      />

      {isWaiting && (
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
          <div className="flex flex-col items-center gap-1">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span className="text-white text-xs font-medium">Waiting...</span>
          </div>
        </div>
      )}

      {isUploading && (
        <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-end p-2">
          <div className="w-full h-1 bg-gray-400 relative overflow-hidden rounded">
            <div
              className="absolute top-0 left-0 h-full bg-white transition-all"
              style={{ width: `${photo.progress ?? 0}%` }}
            />
          </div>
        </div>
      )}

      {isError && (
        <div className="absolute inset-0 bg-red-500 bg-opacity-40 flex items-center justify-center">
          <span className="text-white text-xs font-medium">Upload Failed</span>
        </div>
      )}

      {!isUploading && !isWaiting && !disabled && (
        <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            className="!bg-alpha-600 hover:!bg-alpha-500 backdrop-blur-md"
            loading={Boolean(isMakingDefaultLoading)}
            onClick={onMakeDefault}
          >
            Make default
          </Button>
          <IconButton
            Icon={Trash02}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.PRIMARY}
            className="!bg-alpha-600 hover:!bg-alpha-500 backdrop-blur-md"
            loading={Boolean(isDeletingLoading)}
            onClick={() => onDelete()}
          />
        </div>
      )}

      <Show if={isDefault}>
        <Badge
          size={BadgeSize.SM}
          className="absolute top-3 left-3 !py-1 !border-none !font-bold !text-xxs"
          variant={BadgeVariant.OUTLINE}
        >
          Default
        </Badge>
      </Show>
    </div>
  );
};

type FormattedPhoto = {
  content_type: string;
  resolutions: {
    asset_id: string;
    resolution: string;
  }[];
};

const formatPhotos = (photos: PhotoWithStatus[]): FormattedPhoto[] =>
  photos.map((photo) => ({
    content_type: photo.resolutions.full.file?.type || "",
    resolutions: Object.entries(photo.resolutions).map(
      ([resolution, data]) => ({
        asset_id: data.presigned?.assetId ?? "",
        resolution: resolution
      })
    )
  }));

const withoutPhotos = (ids: string[]) => (photos: PhotoWithStatus[]) =>
  photos.filter((photo) => !ids.includes(photo.id));

const withoutIds = (ids: string[]) => (list: string[]) =>
  list.filter((id) => !ids.includes(id));

const withIds = (ids: string[]) => (list: string[]) => [
  ...list,
  ...ids.filter((id) => !list.includes(id))
];

interface PhotosSectionProps {
  // Gets every completed upload, for forms that submit them all at once (create unit/property).
  onChange?: (formattedPhotos: FormattedPhoto[]) => void;
  // Saves each batch of new uploads right away (unit/property details); once it resolves, the
  // batch is dropped from the local list so a later upload never sends it again.
  onSave?: (formattedPhotos: FormattedPhoto[]) => Promise<unknown>;
  sessionKey: string;
  setPhotosUploading?: (isUploading: boolean) => void;
  isUploading?: boolean;
  uploadedPhotos?: PhotoWithStatus[];
  onDeleteSuccess?: () => void;
  onMakeDefaultSuccess?: () => void;
  disabled?: boolean;
  editDeniedTooltip?: string;
}

export const PhotosForm = ({
  onChange,
  onSave,
  sessionKey,
  setPhotosUploading,
  isUploading,
  uploadedPhotos = [],
  onDeleteSuccess,
  onMakeDefaultSuccess,
  disabled = false,
  editDeniedTooltip
}: PhotosSectionProps) => {
  const isEditDenied = Boolean(editDeniedTooltip);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
    null
  );
  const [photos, setPhotos] = useState<PhotoWithStatus[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const scrollRaf = useRef<number | null>(null);
  const [makingDefaultId, setMakingDefaultId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Uploads already handed to onSave, so one finishing mid-save doesn't resend the others.
  const savingIds = useRef(new Set<string>());
  // Uploads whose save failed: shown as failed (the grid hides other finished uploads once the
  // listing has photos) and sent again with the next change.
  const [failedSaveIds, setFailedSaveIds] = useState<string[]>([]);

  const scrollToTopSmooth = () => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === "function")
      el.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { resize } = useImageResizer();
  const { enqueue } = useUploadQueueWithProgress(5);

  const makeDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      return await instance.post(`portal/v1/listings/media/${id}/make_default`);
    },
    onMutate: (id: string) => {
      setMakingDefaultId(id);
    },
    onSuccess: () => {
      scrollToTopSmooth();
      onMakeDefaultSuccess?.();
    },
    onError: (error) => {
      toast.error("Error making default media:", getErrorMessage(error));
    },
    onSettled: () => {
      setMakingDefaultId(null);
    }
  });

  const deletePhotoMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      return await instance.delete(`portal/v1/listings/media/${mediaId}`);
    },
    onMutate: (mediaId: string) => {
      setDeletingId(mediaId);
    },
    onSuccess: () => {
      onDeleteSuccess?.();
    },
    onError: (error) => {
      toast.error("Error deleting media:", getErrorMessage(error));
    },
    onSettled: () => {
      setDeletingId(null);
    }
  });

  useEffect(() => {
    const hasIncompletePhotos = photos.some((photo) =>
      ["uploading", "waiting"].includes(photo.status)
    );

    if (setPhotosUploading) setPhotosUploading(hasIncompletePhotos);

    if (photos.length === 0 || hasIncompletePhotos) return;

    const completedPhotos = photos.filter(
      (photo) => photo.status === "completed"
    );

    if (!onSave) {
      onChange?.(formatPhotos(completedPhotos));
      return;
    }

    const newPhotos = completedPhotos.filter(
      (photo) => !savingIds.current.has(photo.id)
    );
    if (newPhotos.length === 0) return;

    const ids = newPhotos.map((photo) => photo.id);
    ids.forEach((id) => savingIds.current.add(id));
    onSave(formatPhotos(newPhotos))
      .then(() => {
        setPhotos(withoutPhotos(ids));
        setFailedSaveIds(withoutIds(ids));
      })
      .catch(() => setFailedSaveIds(withIds(ids)))
      .finally(() => ids.forEach((id) => savingIds.current.delete(id)));
  }, [photos, setPhotosUploading]);

  const handleViewPhoto = (index: number) => setSelectedPhotoIndex(index);
  const handleCloseViewer = () => setSelectedPhotoIndex(null);
  const handlePrevious = () =>
    selectedPhotoIndex !== null &&
    setSelectedPhotoIndex(Math.max(selectedPhotoIndex - 1, 0));
  const handleNext = () =>
    selectedPhotoIndex !== null &&
    setSelectedPhotoIndex(Math.min(selectedPhotoIndex + 1, photos.length - 1));

  const recalcScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight > el.clientHeight + 1;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setCanScroll(scrollable);
    setAtBottom(isAtBottom);
  };

  const handleScroll = () => {
    if (scrollRaf.current != null) return;
    scrollRaf.current = window.requestAnimationFrame(() => {
      scrollRaf.current = null;
      recalcScrollState();
    });
  };

  useEffect(() => {
    recalcScrollState();
  }, [photos, uploadedPhotos]);

  useEffect(() => {
    // Recalc on container and window resize
    const el = scrollRef.current;
    if (!el) return;

    const onResize = () => recalcScrollState();
    window.addEventListener("resize", onResize);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => recalcScrollState());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (scrollRaf.current != null) cancelAnimationFrame(scrollRaf.current);
    };
  }, []);

  const updatePhotoById = (id: string, patch: Partial<PhotoWithStatus>) => {
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
    );
  };

  const handleDeletePhoto = (photo: PhotoWithStatus) => {
    if (photo.status === "uploaded") deletePhotoMutation.mutate(photo.id);
    else setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
  };

  const handleDefaultPhoto = (photo: PhotoWithStatus) => {
    if (photo.status === "uploaded") makeDefaultMutation.mutate(photo.id);
    else {
      setPhotos((prev) => {
        const otherPhotos = prev.filter((p) => p.id !== photo.id);
        return [photo, ...otherPhotos];
      });
      requestAnimationFrame(() => {
        scrollToTopSmooth();
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const files = Array.from(fileList);

    const waitingPhotos: PhotoWithStatus[] = await Promise.all(
      files.map(async (file) => {
        const id = `${file.name}-${Date.now()}`;

        const resolutions = {} as PhotoWithStatus["resolutions"];

        for (const res of SUPPORTED_RESOLUTIONS) {
          let resizedFile = file;
          if (res !== ImageResolution.FULL) {
            const blob = await resize(file, res);
            resizedFile = new File([blob], `${res}-${file.name}`, {
              type: file.type
            });
          }

          resolutions[res] = { file: resizedFile };
        }

        const previewUrl = URL.createObjectURL(
          resolutions[ImageResolution.SMALL].file || new Blob()
        );

        return {
          id,
          previewUrl,
          status: "waiting",
          resolutions
        };
      })
    );

    setPhotos((prev) => [...prev, ...waitingPhotos]);

    await Promise.allSettled(
      waitingPhotos.map(async (photo) => {
        const presignedEntries = Object.fromEntries(
          await Promise.all(
            Object.entries(photo.resolutions).map(
              async ([resolution, data]) => {
                const presigned = await getPropertyPresigned(
                  data.file || new File([], ""),
                  sessionKey
                );
                return [
                  resolution,
                  {
                    file: data.file,
                    presigned: {
                      uploadUrl: presigned.response.url,
                      assetId: presigned.asset.id
                    }
                  }
                ];
              }
            )
          )
        );

        enqueue({
          id: photo.id,
          resolutions: presignedEntries,
          onProgress: (percent) =>
            updatePhotoById(photo.id, {
              status: "uploading",
              progress: percent
            }),
          onSuccess: () =>
            updatePhotoById(photo.id, {
              status: "completed",
              progress: 100,
              resolutions: presignedEntries
            }),
          onError: () => updatePhotoById(photo.id, { status: "error" })
        });
      })
    );

    e.target.value = "";
  };

  return (
    <SectionCard
      title="Photos"
      headerExtra={
        <div>
          <Hide
            if={
              (photos.length === 0 && uploadedPhotos.length === 0) || disabled
            }
          >
            <Button
              size={ButtonSize.SM}
              onClick={() => fileInputRef.current?.click()}
              loading={isUploading}
              disabled={disabled || isEditDenied}
              tooltip={editDeniedTooltip}
              tooltipPlacement="bottom-end"
            >
              Upload photo
            </Button>
          </Hide>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={VALID_FILE_TYPES.join(",")}
            className="hidden"
            onChange={handleFileChange}
            disabled={disabled || isEditDenied}
          />
        </div>
      }
    >
      <Show if={photos.length > 0 || uploadedPhotos.length > 0}>
        <div className="relative rounded-lg overflow-hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="max-h-96 md:max-h-[20rem] overflow-auto"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
              {[
                ...uploadedPhotos,
                ...photos.filter(
                  (photo) =>
                    uploadedPhotos.length === 0 ||
                    photo.status !== "completed" ||
                    failedSaveIds.includes(photo.id)
                )
              ].map((photo, index) => {
                return (
                  <PhotoItem
                    key={photo.id + index}
                    photo={
                      failedSaveIds.includes(photo.id)
                        ? { ...photo, status: "error" }
                        : photo
                    }
                    isDefault={index === 0}
                    isMakingDefaultLoading={makingDefaultId === photo.id}
                    isDeletingLoading={deletingId === photo.id}
                    onMakeDefault={() => handleDefaultPhoto(photo)}
                    onDelete={() => handleDeletePhoto(photo)}
                    onViewPhoto={() => handleViewPhoto(index)}
                    disabled={disabled || isEditDenied}
                  />
                );
              })}
            </div>
          </div>

          {canScroll && !atBottom && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white/95 to-transparent dark:from-slate-900/90" />
          )}
        </div>

        {uploadedPhotos.length > 0 ? null : (
          <AssetViewer
            open={selectedPhotoIndex !== null}
            onClose={handleCloseViewer}
            src={
              selectedPhotoIndex !== null
                ? uploadedPhotos[selectedPhotoIndex]?.resolutions.small
                    ?.uploadedUrl ||
                  photos[selectedPhotoIndex].resolutions.small.presigned?.url ||
                  URL.createObjectURL(
                    photos[selectedPhotoIndex].resolutions.full.file ||
                      new Blob()
                  )
                : ""
            }
            filename={
              selectedPhotoIndex !== null
                ? photos[selectedPhotoIndex].resolutions.full.file?.name || ""
                : ""
            }
            mimeType={
              selectedPhotoIndex !== null
                ? photos[selectedPhotoIndex].resolutions.full.file?.type || ""
                : ""
            }
            fileSize={
              selectedPhotoIndex !== null
                ? photos[selectedPhotoIndex].resolutions.full.file?.size || 0
                : 0
            }
            currentIndex={selectedPhotoIndex || 0}
            totalItems={photos.length}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        )}
      </Show>

      <Show if={photos.length === 0 && uploadedPhotos.length === 0}>
        <div className="py-12 px-8 text-center">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-alpha-40 rounded-full flex items-center justify-center">
              <HugeiconsIcon icon={Image02Icon} />
            </div>
            <h3 className="text-lg mb-4 text-content-300">
              No photos uploaded yet
            </h3>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              onClick={() => fileInputRef.current?.click()}
              className="mx-auto"
              Icon={Plus}
              disabled={disabled || isEditDenied}
              tooltip={editDeniedTooltip}
            >
              Upload photo
            </Button>
          </div>
        </div>
      </Show>
    </SectionCard>
  );
};
